import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Car, Image, Settings, LogOut, Save, Send, MapPin, Users,
  AlertCircle, CheckCircle, Clock, X, DollarSign, Shield, Bell, MessageCircle,
  Plane, Wifi, Snowflake, Music, Battery, Wine, Plus, Trash2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { MediaGallery, ImageUrlGallery } from '../../components/MediaGallery';
import { NotificationsPanel } from '../../components/NotificationBell';
import { UserInbox } from '../../components/Inbox';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sidebar = ({ activeTab, setActiveTab }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const menuItems = [
    { id: 'profile', icon: Car, label: 'Mi Servicio' },
    { id: 'vehicles', icon: Settings, label: 'Vehículos y Precios' },
    { id: 'media', icon: Image, label: 'Galería' },
    { id: 'messages', icon: MessageCircle, label: 'Mensajes' },
    { id: 'notifications', icon: Bell, label: 'Notificaciones' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#0A0A0A] border-r border-white/5 flex flex-col z-40">
      <div className="p-6 border-b border-white/5">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="/gs-agency-logo.png" 
            alt="GS Agency" 
            className="h-10 w-auto object-contain"
          />
        </Link>
      </div>

      <nav className="flex-1 p-4 overflow-y-auto">
        <p className="text-white/40 text-xs uppercase tracking-wider px-4 mb-4">Panel de Transporte</p>
        <div className="space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
                activeTab === item.id
                  ? 'bg-white/10 text-white'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`tab-${item.id}`}
            >
              <item.icon size={18} />
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3 px-4 py-3 mb-2">
          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
            <Car size={16} className="text-white/60" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm truncate">{user?.name}</p>
            <p className="text-white/40 text-xs">Proveedor de Transporte</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition-colors text-sm"
          data-testid="logout-btn"
        >
          <LogOut size={18} />
          Cerrar sesión
        </button>
      </div>
    </aside>
  );
};

const StatusBadge = ({ status }) => {
  const config = {
    draft: { bg: 'bg-white/10', text: 'text-white/60', icon: Clock, label: 'Borrador' },
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock, label: 'Pendiente de Aprobación' },
    approved: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle, label: 'Aprobado y Visible' },
    rejected: { bg: 'bg-red-500/20', text: 'text-red-400', icon: X, label: 'Rechazado' },
    changes_requested: { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: AlertCircle, label: 'Cambios Solicitados' },
  };

  const { bg, text, icon: Icon, label } = config[status] || config.draft;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${bg}`}>
      <Icon size={14} className={text} />
      <span className={`text-sm ${text}`}>{label}</span>
    </div>
  );
};

const vehicleTypeOptions = [
  'Automóvil Particular', 'Automóvil Ejecutivo', 'Automóvil de Lujo', 'Vehículo Blindado',
  'Camioneta', 'SUV', 'Van', 'Sprinter', 'Minibús', 'Autobús'
];

const serviceLevelOptions = ['Básico', 'Ejecutivo', 'Premium', 'Lujo', 'VIP', 'Blindado'];

const serviceCategoryOptions = [
  'Transporte para Staff', 'Transporte para Artistas', 'Transporte para Clientes VIP',
  'Logística de Traslados para Eventos'
];

const amenityOptions = [
  'Aire acondicionado', 'WiFi', 'Cargadores', 'Bebidas', 'Interior de lujo',
  'Sonido premium', 'Cristales polarizados', 'Seguridad adicional', 'Espacio para equipo',
  'Asistencia bilingüe', 'Chofer bilingüe', 'Facturación disponible'
];

export default function TransportDashboard() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    business_name: { es: '', en: '' },
    provider_name: '',
    slug: '',
    description: { es: '', en: '' },
    city: '',
    country: 'México',
    coverage_areas: [],
    phone: '',
    whatsapp: '',
    email: '',
    social_links: {},
    logo: '',
    cover_image: '',
    gallery: [],
    vehicle_types: [],
    brand: '',
    model_name: '',
    year: '',
    passenger_capacity: 0,
    luggage_capacity: '',
    color: '',
    units_available: 1,
    with_driver: true,
    without_driver: false,
    by_hour: true,
    by_day: true,
    by_transfer: true,
    by_event: true,
    night_availability: false,
    out_of_town: false,
    airport_service: false,
    tours_long_trips: false,
    service_levels: [],
    service_categories: [],
    requirements: {
      id_required: false,
      license_required: false,
      deposit_required: false,
      contract_required: false,
      advance_payment: false,
      mileage_limit: false,
      min_age: 21,
      usage_conditions: '',
      cancellation_policy: ''
    },
    pricing: {
      base_price: 0,
      price_per_hour: 0,
      price_per_day: 0,
      price_per_transfer: 0,
      price_per_event: 0,
      extra_time_cost: 0,
      driver_cost: 0,
      fuel_cost: 0,
      travel_expenses: 0,
      extra_mileage_cost: 0,
      quote_only: false
    },
    currency: 'MXN',
    amenities: []
  });

  const [newCoverageArea, setNewCoverageArea] = useState('');

  useEffect(() => {
    fetchProfile();
  }, [token]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/transport/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setProfile(response.data);
        setForm({ ...form, ...response.data });
      }
    } catch (error) {
      console.log('No profile found, will create new one');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleSave = async (submit = false) => {
    setSaving(true);
    try {
      const data = {
        ...form,
        slug: form.slug || generateSlug(form.business_name.es || form.business_name.en || 'servicio')
      };

      if (profile) {
        await axios.put(`${API}/transport/profile`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/transport/profile`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (submit) {
        await axios.post(`${API}/transport/profile/submit`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Perfil enviado para revisión');
      } else {
        toast.success('Cambios guardados');
      }

      fetchProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  const addCoverageArea = () => {
    if (newCoverageArea && !form.coverage_areas.includes(newCoverageArea)) {
      setForm({ ...form, coverage_areas: [...form.coverage_areas, newCoverageArea] });
      setNewCoverageArea('');
    }
  };

  const removeCoverageArea = (area) => {
    setForm({ ...form, coverage_areas: form.coverage_areas.filter(a => a !== area) });
  };

  const toggleArrayItem = (field, value) => {
    const current = form[field] || [];
    if (current.includes(value)) {
      setForm({ ...form, [field]: current.filter(v => v !== value) });
    } else {
      setForm({ ...form, [field]: [...current, value] });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="transport-dashboard">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />

      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-display text-white mb-2">
              {activeTab === 'profile' && 'Información del Servicio'}
              {activeTab === 'vehicles' && 'Vehículos y Precios'}
              {activeTab === 'media' && 'Galería de Imágenes'}
              {activeTab === 'messages' && 'Mensajes'}
              {activeTab === 'notifications' && 'Notificaciones'}
            </h1>
            {profile?.status && <StatusBadge status={profile.status} />}
          </div>

          {activeTab !== 'messages' && activeTab !== 'notifications' && (
            <div className="flex gap-3">
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/15 transition-colors disabled:opacity-50"
                data-testid="save-draft-btn"
              >
                <Save size={18} />
                {saving ? 'Guardando...' : 'Guardar Borrador'}
              </button>
              <button
                onClick={() => handleSave(true)}
                disabled={saving || profile?.status === 'pending'}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#B8962E] transition-colors disabled:opacity-50"
                data-testid="submit-btn"
              >
                <Send size={18} />
                Enviar a Revisión
              </button>
            </div>
          )}
        </div>

        {/* Review Notes */}
        {profile?.review_notes?.length > 0 && profile?.status === 'changes_requested' && (
          <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
            <h3 className="text-orange-400 font-semibold mb-2">Cambios Solicitados</h3>
            <p className="text-white/70">{profile.review_notes[profile.review_notes.length - 1]?.notes}</p>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6" data-testid="profile-tab">
            {/* Basic Info */}
            <div className="glass-dark rounded-xl p-6 border border-white/5">
              <h2 className="text-white text-lg font-semibold mb-4">Datos Generales</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Nombre Comercial (Español) *</label>
                  <Input
                    value={form.business_name.es}
                    onChange={(e) => setForm({ ...form, business_name: { ...form.business_name, es: e.target.value } })}
                    placeholder="Ej: Elite Transport CDMX"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="business-name-es"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Nombre Comercial (Inglés)</label>
                  <Input
                    value={form.business_name.en}
                    onChange={(e) => setForm({ ...form, business_name: { ...form.business_name, en: e.target.value } })}
                    placeholder="Ej: Elite Transport CDMX"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Nombre del Proveedor o Empresa</label>
                  <Input
                    value={form.provider_name}
                    onChange={(e) => setForm({ ...form, provider_name: e.target.value })}
                    placeholder="Ej: Transportes Elite S.A. de C.V."
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Ciudad o Zona de Operación *</label>
                  <Input
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="Ciudad de México"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="city-input"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">País</label>
                  <Input
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    placeholder="México"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-white/70 text-sm mb-2">Descripción del Servicio (Español)</label>
                  <Textarea
                    value={form.description.es}
                    onChange={(e) => setForm({ ...form, description: { ...form.description, es: e.target.value } })}
                    placeholder="Describe tu servicio de transporte..."
                    rows={4}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="description-es"
                  />
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="glass-dark rounded-xl p-6 border border-white/5">
              <h2 className="text-white text-lg font-semibold mb-4">Información de Contacto</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Teléfono</label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+52 55 1234 5678"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">WhatsApp</label>
                  <Input
                    value={form.whatsapp}
                    onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                    placeholder="+52 55 1234 5678"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Correo Electrónico</label>
                  <Input
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="contacto@ejemplo.com"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Coverage Areas */}
            <div className="glass-dark rounded-xl p-6 border border-white/5">
              <h2 className="text-white text-lg font-semibold mb-4">Cobertura</h2>
              <div className="flex gap-2 mb-4">
                <Input
                  value={newCoverageArea}
                  onChange={(e) => setNewCoverageArea(e.target.value)}
                  placeholder="Agregar zona de cobertura..."
                  className="bg-white/5 border-white/10 text-white flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && addCoverageArea()}
                />
                <button
                  onClick={addCoverageArea}
                  className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#B8962E] transition-colors"
                >
                  <Plus size={20} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.coverage_areas.map((area, index) => (
                  <span key={index} className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 text-white rounded-full text-sm">
                    {area}
                    <button onClick={() => removeCoverageArea(area)} className="text-white/50 hover:text-white">
                      <X size={14} />
                    </button>
                  </span>
                ))}
                {form.coverage_areas.length === 0 && (
                  <p className="text-white/40 text-sm">No hay zonas de cobertura agregadas</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Vehicles & Pricing Tab */}
        {activeTab === 'vehicles' && (
          <div className="space-y-6" data-testid="vehicles-tab">
            {/* Vehicle Types */}
            <div className="glass-dark rounded-xl p-6 border border-white/5">
              <h2 className="text-white text-lg font-semibold mb-4">Tipos de Vehículo</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {vehicleTypeOptions.map((type) => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.vehicle_types.includes(type)}
                      onChange={() => toggleArrayItem('vehicle_types', type)}
                      className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                    />
                    <span className="text-white/70 text-sm">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Vehicle Details */}
            <div className="glass-dark rounded-xl p-6 border border-white/5">
              <h2 className="text-white text-lg font-semibold mb-4">Detalles del Vehículo</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Marca(s)</label>
                  <Input
                    value={form.brand}
                    onChange={(e) => setForm({ ...form, brand: e.target.value })}
                    placeholder="Ej: Mercedes-Benz / BMW"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Modelo(s)</label>
                  <Input
                    value={form.model_name}
                    onChange={(e) => setForm({ ...form, model_name: e.target.value })}
                    placeholder="Ej: Clase S / Serie 7"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Año</label>
                  <Input
                    value={form.year}
                    onChange={(e) => setForm({ ...form, year: e.target.value })}
                    placeholder="Ej: 2023-2024"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Capacidad de Pasajeros</label>
                  <Input
                    type="number"
                    value={form.passenger_capacity}
                    onChange={(e) => setForm({ ...form, passenger_capacity: parseInt(e.target.value) || 0 })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Capacidad de Equipaje</label>
                  <Input
                    value={form.luggage_capacity}
                    onChange={(e) => setForm({ ...form, luggage_capacity: e.target.value })}
                    placeholder="Ej: 4 maletas grandes"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Color</label>
                  <Input
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    placeholder="Negro"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Número de Unidades Disponibles</label>
                  <Input
                    type="number"
                    value={form.units_available}
                    onChange={(e) => setForm({ ...form, units_available: parseInt(e.target.value) || 1 })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Service Levels */}
            <div className="glass-dark rounded-xl p-6 border border-white/5">
              <h2 className="text-white text-lg font-semibold mb-4">Niveles de Servicio</h2>
              <div className="flex flex-wrap gap-3">
                {serviceLevelOptions.map((level) => (
                  <label key={level} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.service_levels.includes(level.toLowerCase())}
                      onChange={() => toggleArrayItem('service_levels', level.toLowerCase())}
                      className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                    />
                    <span className="text-white/70 text-sm">{level}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Service Modes */}
            <div className="glass-dark rounded-xl p-6 border border-white/5">
              <h2 className="text-white text-lg font-semibold mb-4">Modalidad del Servicio</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.with_driver}
                    onChange={(e) => setForm({ ...form, with_driver: e.target.checked })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">Con chofer</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.without_driver}
                    onChange={(e) => setForm({ ...form, without_driver: e.target.checked })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">Sin chofer</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.by_hour}
                    onChange={(e) => setForm({ ...form, by_hour: e.target.checked })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">Por hora</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.by_day}
                    onChange={(e) => setForm({ ...form, by_day: e.target.checked })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">Por día</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.by_transfer}
                    onChange={(e) => setForm({ ...form, by_transfer: e.target.checked })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">Por traslado</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.by_event}
                    onChange={(e) => setForm({ ...form, by_event: e.target.checked })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">Por evento</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.night_availability}
                    onChange={(e) => setForm({ ...form, night_availability: e.target.checked })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">Nocturno</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.out_of_town}
                    onChange={(e) => setForm({ ...form, out_of_town: e.target.checked })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">Foráneo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.airport_service}
                    onChange={(e) => setForm({ ...form, airport_service: e.target.checked })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm flex items-center gap-1"><Plane size={14} /> Aeropuerto</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.tours_long_trips}
                    onChange={(e) => setForm({ ...form, tours_long_trips: e.target.checked })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">Giras / Largos</span>
                </label>
              </div>
            </div>

            {/* Pricing */}
            <div className="glass-dark rounded-xl p-6 border border-white/5">
              <h2 className="text-white text-lg font-semibold mb-4 flex items-center gap-2">
                <DollarSign size={20} className="text-[#D4AF37]" />
                Precios (MXN)
              </h2>
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.pricing.quote_only}
                    onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, quote_only: e.target.checked } })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">Solo cotizar (no mostrar precios fijos)</span>
                </label>
              </div>
              {!form.pricing.quote_only && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Precio por hora</label>
                    <Input
                      type="number"
                      value={form.pricing.price_per_hour}
                      onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, price_per_hour: parseFloat(e.target.value) || 0 } })}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Precio por día</label>
                    <Input
                      type="number"
                      value={form.pricing.price_per_day}
                      onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, price_per_day: parseFloat(e.target.value) || 0 } })}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Precio por traslado</label>
                    <Input
                      type="number"
                      value={form.pricing.price_per_transfer}
                      onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, price_per_transfer: parseFloat(e.target.value) || 0 } })}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Precio por evento</label>
                    <Input
                      type="number"
                      value={form.pricing.price_per_event}
                      onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, price_per_event: parseFloat(e.target.value) || 0 } })}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Costo tiempo extra (hr)</label>
                    <Input
                      type="number"
                      value={form.pricing.extra_time_cost}
                      onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, extra_time_cost: parseFloat(e.target.value) || 0 } })}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Costo chofer</label>
                    <Input
                      type="number"
                      value={form.pricing.driver_cost}
                      onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, driver_cost: parseFloat(e.target.value) || 0 } })}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Amenities */}
            <div className="glass-dark rounded-xl p-6 border border-white/5">
              <h2 className="text-white text-lg font-semibold mb-4">Amenidades</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {amenityOptions.map((amenity) => (
                  <label key={amenity} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.amenities.includes(amenity.toLowerCase())}
                      onChange={() => toggleArrayItem('amenities', amenity.toLowerCase())}
                      className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                    />
                    <span className="text-white/70 text-sm">{amenity}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Requirements */}
            <div className="glass-dark rounded-xl p-6 border border-white/5">
              <h2 className="text-white text-lg font-semibold mb-4">Requisitos</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.requirements.id_required}
                    onChange={(e) => setForm({ ...form, requirements: { ...form.requirements, id_required: e.target.checked } })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">Identificación oficial</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.requirements.contract_required}
                    onChange={(e) => setForm({ ...form, requirements: { ...form.requirements, contract_required: e.target.checked } })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">Firma de contrato</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.requirements.advance_payment}
                    onChange={(e) => setForm({ ...form, requirements: { ...form.requirements, advance_payment: e.target.checked } })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">Pago anticipado</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.requirements.deposit_required}
                    onChange={(e) => setForm({ ...form, requirements: { ...form.requirements, deposit_required: e.target.checked } })}
                    className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#D4AF37]"
                  />
                  <span className="text-white/70 text-sm">Depósito en garantía</span>
                </label>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Política de Cancelación</label>
                <Textarea
                  value={form.requirements.cancellation_policy}
                  onChange={(e) => setForm({ ...form, requirements: { ...form.requirements, cancellation_policy: e.target.value } })}
                  placeholder="Ej: Cancelación gratuita con 24 horas de anticipación..."
                  rows={3}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
          </div>
        )}

        {/* Media Tab */}
        {activeTab === 'media' && (
          <div className="space-y-6" data-testid="media-tab">
            <div className="glass-dark rounded-xl p-6 border border-white/5">
              <h2 className="text-white text-lg font-semibold mb-4">Imagen Principal</h2>
              <div className="mb-4">
                <label className="block text-white/70 text-sm mb-2">URL de imagen de portada</label>
                <Input
                  value={form.cover_image || ''}
                  onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                  placeholder="https://ejemplo.com/imagen.jpg"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              {form.cover_image && (
                <img src={form.cover_image} alt="Cover" className="w-full max-w-md h-48 object-cover rounded-lg" />
              )}
            </div>

            <div className="glass-dark rounded-xl p-6 border border-white/5">
              <h2 className="text-white text-lg font-semibold mb-4">Galería de Vehículos</h2>
              <ImageUrlGallery
                images={form.gallery || []}
                onChange={(images) => setForm({ ...form, gallery: images })}
                maxImages={10}
              />
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div data-testid="messages-tab">
            <UserInbox token={token} user={user} />
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div data-testid="notifications-tab">
            <NotificationsPanel token={token} userId={user?.user_id} />
          </div>
        )}
      </main>
    </div>
  );
}
